import { jsPDF } from "jspdf";

// Colors
const COLORS = {
    primary: [139, 92, 246],      // Violet
    secondary: [168, 85, 247],    // Purple
    dark: [10, 10, 10],
    text: [30, 30, 30],
    textLight: [100, 100, 100],
    success: [16, 185, 129],
    warning: [245, 158, 11],
    danger: [239, 68, 68],
    white: [255, 255, 255],
    bronze: [205, 127, 50],
    silver: [192, 192, 192],
    gold: [255, 215, 0]
};

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

// Helper to add page number
const addPageNumber = (doc, pageNum) => {
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.text(`${pageNum}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
};

// Helper to add footer
const addFooter = (doc) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textLight);
    doc.text("Generated by SiteGradePro.com", doc.internal.pageSize.getWidth() / 2, pageHeight - 5, { align: 'center' });
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

// Main PDF Generator
export async function generateReferralPlanPDF(data) {
    const {
        businessName = "Your Business",
        websiteUrl = "yourwebsite.com",
        inputs = {},
        results = {},
        leadData = null
    } = data;

    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let pageNum = 1;

    // Try to load logo image
    let logoImg = null;
    try {
        const response = await fetch(LOGO_URL);
        const blob = await response.blob();
        logoImg = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        console.log("Could not load logo for PDF");
    }

    // ========== PAGE 1: COVER PAGE ==========
    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 100, 'F');
    
    // Accent gradient line
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 98, pageWidth / 2, 3, 'F');
    doc.setFillColor(...COLORS.secondary);
    doc.rect(pageWidth / 2, 98, pageWidth / 2, 3, 'F');

    // Logo text
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text("SITE GRADE PRO", margin, 30);

    // Main title
    doc.setFontSize(32);
    doc.setTextColor(...COLORS.white);
    doc.text("Referral Program", margin, 55);
    doc.setTextColor(...COLORS.secondary);
    doc.text("Blueprint", margin, 70);
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.white);
    doc.text("TM", margin + 52, 62);

    // Report details box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, 115, contentWidth, 50, 3, 3, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text("CUSTOM REFERRAL PROGRAM FOR:", margin + 10, 128);
    
    doc.setFontSize(18);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text(leadData?.name || businessName, margin + 10, 142);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.primary);
    doc.text(leadData?.website || websiteUrl, margin + 10, 152);
    
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text(new Date().toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
    }), margin + 10, 160);

    // Key metric preview
    const metricX = pageWidth - margin - 55;
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(metricX - 5, 115, 65, 50, 3, 3, 'F');
    doc.setDrawColor(...COLORS.primary);
    doc.setLineWidth(0.5);
    doc.roundedRect(metricX - 5, 115, 65, 50, 3, 3, 'S');
    
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.text("BASE COMMISSION", metricX + 27, 128, { align: 'center' });
    
    doc.setFontSize(22);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(results.baseCommission || 0), metricX + 27, 148, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.text("per referral", metricX + 27, 158, { align: 'center' });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGE 2: PROGRAM OVERVIEW ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("PROGRAM OVERVIEW", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    let yPos = 40;

    // Input Summary
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Your Business Parameters", margin, yPos);
    yPos += 15;

    const inputItems = [
        { label: "Product Price", value: formatCurrency(inputs.price || 0) },
        { label: "Profit Margin", value: `${inputs.margin || 0}%` },
        { label: "Monthly Sales Goal", value: `${inputs.salesGoal || 0} sales` },
        { label: "Growth Strategy", value: inputs.growthSpeed?.name || "Moderate Growth" }
    ];

    inputItems.forEach((item, i) => {
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, yPos, contentWidth, 12, 2, 2, 'F');
        
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.textLight);
        doc.setFont('helvetica', 'normal');
        doc.text(item.label, margin + 8, yPos + 8);
        
        doc.setTextColor(...COLORS.text);
        doc.setFont('helvetica', 'bold');
        doc.text(item.value, pageWidth - margin - 8, yPos + 8, { align: 'right' });
        
        yPos += 16;
    });

    yPos += 15;

    // Program Score
    doc.setFillColor(...COLORS.primary);
    doc.roundedRect(margin, yPos, contentWidth, 35, 3, 3, 'F');
    
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("Projected ROI Score", margin + 15, yPos + 15);
    
    doc.setFontSize(28);
    doc.text(`${results.score || 0}`, pageWidth - margin - 30, yPos + 22, { align: 'right' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`/ ${results.maxScore || 200}`, pageWidth - margin - 8, yPos + 22, { align: 'right' });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGE 3: TIER STRUCTURE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("3-TIER COMMISSION STRUCTURE", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 40;

    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Your Customized Tier System", margin, yPos);
    yPos += 5;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.textLight);
    doc.text("Reward your top performers with escalating commissions", margin, yPos + 5);
    yPos += 20;

    const tierColors = [COLORS.bronze, COLORS.silver, COLORS.gold];
    const tiers = results.tiers || [
        { name: "Bronze", commission: 0, referrals: "1-5", description: "Entry level" },
        { name: "Silver", commission: 0, referrals: "6-15", description: "Growing referrer" },
        { name: "Gold", commission: 0, referrals: "16+", description: "Top performer" }
    ];

    tiers.forEach((tier, i) => {
        const tierY = yPos + (i * 55);
        
        // Tier card
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, tierY, contentWidth, 48, 3, 3, 'F');
        doc.setDrawColor(...tierColors[i]);
        doc.setLineWidth(1);
        doc.roundedRect(margin, tierY, contentWidth, 48, 3, 3, 'S');
        
        // Tier badge
        doc.setFillColor(...tierColors[i]);
        doc.roundedRect(margin + 8, tierY + 8, 25, 8, 2, 2, 'F');
        doc.setFontSize(7);
        doc.setTextColor(i === 1 ? 50 : 255, i === 1 ? 50 : 255, i === 1 ? 50 : 255);
        doc.setFont('helvetica', 'bold');
        doc.text(tier.name.toUpperCase(), margin + 20.5, tierY + 13.5, { align: 'center' });
        
        // Commission
        doc.setFontSize(24);
        doc.setTextColor(...COLORS.text);
        doc.text(formatCurrency(tier.commission), margin + 50, tierY + 25);
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.textLight);
        doc.text("per referral", margin + 50, tierY + 35);
        
        // Requirements
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.text);
        doc.setFont('helvetica', 'bold');
        doc.text("Requirements:", pageWidth - margin - 70, tierY + 18);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.textLight);
        doc.text(`${tier.referrals} referrals/month`, pageWidth - margin - 70, tierY + 28);
        doc.text(tier.description, pageWidth - margin - 70, tierY + 38);
    });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGE 4: FINANCIAL PROJECTIONS ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("FINANCIAL PROJECTIONS", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 40;

    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Revenue Impact Analysis", margin, yPos);
    yPos += 20;

    // Key metrics grid
    const metrics = [
        { label: "Cost Per Acquisition", value: formatCurrency(results.baseCommission || 0), color: COLORS.primary },
        { label: "Est. Referral Revenue", value: formatCurrency(results.estimatedRevenue || 0), color: COLORS.success },
        { label: "Projected ROI", value: `${Math.round((results.estimatedROI || 0) * 100)}%`, color: COLORS.gold },
        { label: "Break-even Point", value: `${Math.ceil(1 / ((inputs.margin || 20) / 100))} sales`, color: COLORS.secondary }
    ];

    const gridWidth = (contentWidth - 10) / 2;
    metrics.forEach((metric, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = margin + (col * (gridWidth + 10));
        const y = yPos + (row * 45);
        
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(x, y, gridWidth, 38, 3, 3, 'F');
        
        doc.setFontSize(9);
        doc.setTextColor(...COLORS.textLight);
        doc.setFont('helvetica', 'bold');
        doc.text(metric.label.toUpperCase(), x + 10, y + 12);
        
        doc.setFontSize(20);
        doc.setTextColor(...metric.color);
        doc.setFont('helvetica', 'bold');
        doc.text(metric.value, x + 10, y + 30);
    });

    yPos += 105;

    // ROI explanation
    doc.setFillColor(...COLORS.success);
    doc.setGlobalAlpha && doc.setGlobalAlpha(0.1);
    doc.roundedRect(margin, yPos, contentWidth, 40, 3, 3, 'F');
    doc.setGlobalAlpha && doc.setGlobalAlpha(1);
    doc.setDrawColor(...COLORS.success);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, yPos, contentWidth, 40, 3, 3, 'S');
    
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.success);
    doc.setFont('helvetica', 'bold');
    doc.text("WHY THIS WORKS", margin + 10, yPos + 12);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.text);
    doc.setFontSize(9);
    const roiText = `With a ${inputs.margin || 0}% profit margin, each ${formatCurrency(inputs.price || 0)} sale generates ${formatCurrency((inputs.price || 0) * (inputs.margin || 0) / 100)} profit. Your base commission of ${formatCurrency(results.baseCommission || 0)} means you're paying ${Math.round(((results.baseCommission || 0) / ((inputs.price || 0) * (inputs.margin || 0) / 100)) * 100)}% of profit per referral - a sustainable acquisition cost.`;
    const roiLines = doc.splitTextToSize(roiText, contentWidth - 20);
    doc.text(roiLines, margin + 10, yPos + 22);

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGE 5: IMPLEMENTATION GUIDE ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("IMPLEMENTATION GUIDE", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 40;

    doc.setFontSize(14);
    doc.setTextColor(...COLORS.text);
    doc.setFont('helvetica', 'bold');
    doc.text("Launch Your Program in 4 Steps", margin, yPos);
    yPos += 15;

    const steps = [
        { title: "Choose Your Platform", description: "Select a referral software: ReferralCandy, Refersion, or built-in Shopify tools", time: "Day 1-2" },
        { title: "Set Up Tier Structure", description: "Configure your Bronze, Silver, and Gold tiers with the commission rates defined in this plan", time: "Day 3-4" },
        { title: "Create Marketing Assets", description: "Design referral links, social share buttons, and email templates for referrers", time: "Day 5-6" },
        { title: "Launch & Promote", description: "Announce to existing customers via email, add to website, and incentivize first referrers", time: "Day 7" }
    ];

    steps.forEach((step, i) => {
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, yPos, contentWidth, 30, 3, 3, 'F');
        
        // Step number
        doc.setFillColor(...COLORS.primary);
        doc.circle(margin + 12, yPos + 15, 8, 'F');
        doc.setFontSize(12);
        doc.setTextColor(...COLORS.white);
        doc.setFont('helvetica', 'bold');
        doc.text(`${i + 1}`, margin + 12, yPos + 18, { align: 'center' });
        
        // Content
        doc.setFontSize(11);
        doc.setTextColor(...COLORS.text);
        doc.text(step.title, margin + 28, yPos + 12);
        
        doc.setFontSize(9);
        doc.setTextColor(...COLORS.textLight);
        doc.setFont('helvetica', 'normal');
        doc.text(step.description, margin + 28, yPos + 22);
        
        // Timeline
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.primary);
        doc.text(step.time, pageWidth - margin - 8, yPos + 12, { align: 'right' });
        
        yPos += 36;
    });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGE 6: RECOMMENDED TOOLS ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("RECOMMENDED TOOLS", margin, 16);
    // Add logo to header
    if (logoImg) {
        try { doc.addImage(logoImg, 'PNG', pageWidth - margin - 30, 5, 28, 14); } catch (e) {}
    }

    yPos = 40;

    const tools = [
        { category: "Referral Platforms", options: ["ReferralCandy", "Refersion", "Ambassador"] },
        { category: "Tracking & Analytics", options: ["Google Analytics (free)", "Mixpanel"] },
        { category: "Email Automation", options: ["Klaviyo", "Mailchimp"] },
        { category: "Payout Management", options: ["PayPal Business", "Stripe Connect"] }
    ];

    tools.forEach((tool, i) => {
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, yPos, contentWidth, 22, 2, 2, 'F');
        
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.text);
        doc.setFont('helvetica', 'bold');
        doc.text(tool.category, margin + 8, yPos + 9);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.textLight);
        doc.text(tool.options.join("  |  "), margin + 8, yPos + 17);
        
        yPos += 28;
    });

    addFooter(doc);
    addPageNumber(doc, pageNum);

    // ========== PAGE 7: CLOSING ==========
    doc.addPage();
    pageNum++;

    doc.setFillColor(...COLORS.dark);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Accent bar
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, pageHeight / 2 - 50, pageWidth / 2, 3, 'F');
    doc.setFillColor(...COLORS.secondary);
    doc.rect(pageWidth / 2, pageHeight / 2 - 50, pageWidth / 2, 3, 'F');

    doc.setFontSize(22);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("Turn Your Customers Into", pageWidth / 2, pageHeight / 2 - 25, { align: 'center' });
    doc.setTextColor(...COLORS.secondary);
    doc.text("Your Sales Team", pageWidth / 2, pageHeight / 2 - 5, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'normal');
    const closingText = "This customized referral program blueprint is designed based on your specific business metrics. Implementation of this structure can significantly increase customer acquisition while maintaining healthy profit margins.";
    const closingLines = doc.splitTextToSize(closingText, contentWidth - 20);
    doc.text(closingLines, pageWidth / 2, pageHeight / 2 + 25, { align: 'center' });

    // CTA with clickable email link
    const buttonX = pageWidth / 2 - 50;
    const buttonY = pageHeight / 2 + 55;
    const buttonW = 100;
    const buttonH = 14;
    
    doc.setFillColor(...COLORS.primary);
    doc.roundedRect(buttonX, buttonY, buttonW, buttonH, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.text("Schedule Consultation", pageWidth / 2, pageHeight / 2 + 64, { align: 'center' });
    
    // Add clickable email link
    doc.link(buttonX, buttonY, buttonW, buttonH, { url: 'mailto:hello@sitegradepro.com?subject=Referral%20Program%20-%20Consultation%20Request' });

    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textLight);
    doc.text("Email: hello@sitegradepro.com", pageWidth / 2, pageHeight / 2 + 80, { align: 'center' });

    // Logo
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text("SITE GRADE PRO", pageWidth / 2, pageHeight - 40, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.textLight);
    doc.setFont('helvetica', 'normal');
    doc.text("SiteGradePro.com", pageWidth / 2, pageHeight - 30, { align: 'center' });

    addPageNumber(doc, pageNum);

    return doc;
}

// Export function to download the PDF
export async function downloadReferralPlanPDF(data) {
    const doc = await generateReferralPlanPDF(data);
    const fileName = `referral-program-blueprint-${data.leadData?.website?.replace(/[^a-z0-9]/gi, '-') || 'report'}.pdf`;
    doc.save(fileName);
    return fileName;
}
